import { Animation, ImageContainer, ObjectFit } from './ImageContainer';
import React, { useCallback, useEffect, useState } from 'react';

import ButtonControls from './ButtonControls';
import IconButton from '../generic/IconButton';
import { IconTypes } from '../generic/Icon';
import Modal from '../generic/Modal';
import PrevNextControls from './PrevNextControls';
import TextContainer from './TextContainer';
import { mergeClassNames } from '../../utils/mergeClassnames';
import { saveAs } from 'file-saver';
import styles from './index.module.scss';

export { Animation };

/**
 * Gallery
 *
 * @param {object} param
 * @param {object[]} param.slides
 * @param {string} [param.className] - null
 * @param {object} [param.style] - null
 * @param {string} [param.animation] - Animation.pan

 * @param {Boolean} [param.showHeading] - true
 * @param {Boolean} [param.showButtonControls] - true
 * @param {Boolean} [param.showArrowControls] - true
 * @param {Boolean} [param.showTextArea] - true
 * @param {Boolean} [param.showFullscreenControls] - true
 * @param {Boolean} [param.showDownloadControls] - true
 * 
 * @param {Number} [param.overrideSlideIndex] - -1
 */

export default function Gallery(param) {
  let [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen, setIsFullscreen]);

  let overrideSlideIndex = param.overrideSlideIndex;
  let [activeSlideIndex, setActiveSlideIndex] = useState(0);
  useEffect(() => {
    if (overrideSlideIndex !== -1) {
      setActiveSlideIndex(overrideSlideIndex);
    }
  }, [overrideSlideIndex]);

  const sharedProps = {
    ...{
      ...param,
      isFullscreen,
      toggleFullscreen,
      showTextArea: isFullscreen ? false : param.showTextArea,
      activeSlideIndex,
      setActiveSlideIndex,
    },
  };

  return (
    <>
      <GalleryContainer {...sharedProps} />
      {isFullscreen && (
        <Modal className={styles.modal} closeCallback={toggleFullscreen} portal lockBodyScroll>
          <GalleryContainer {...sharedProps} />
        </Modal>
      )}
    </>
  );
}

/**
 * Gallery Container
 *
 * @param {object} param
 * @param {object[]} param.slides
 * @param {string} [param.className] - null
 * @param {object} [param.style] - null
 * @param {string} [param.animation] - Animation.pan

 * @param {Boolean} [param.showHeading] - true
 * @param {Boolean} [param.showButtonControls] - true
 * @param {Boolean} [param.showArrowControls] - true
 * @param {Boolean} [param.showTextArea] - true
 * @param {Boolean} [param.showFullscreenControls] - true
 * @param {Boolean} [param.showDownloadControls] - true
 * 
 * @param {Boolean} [param.isFullscreen] - false
 * @param {Function} [param.toggleFullscreen] - null
 * 
 * @param {Number} [param.activeSlideIndex] - 0
 * @param {Function} [param.setActiveSlideIndex] - null
 */

function GalleryContainer({
  slides = [],
  className = null,
  style = null,
  animation = Animation.pan,

  showHeading = true,
  showButtonControls = true,
  showArrowControls = true,
  showTextArea = true,
  showFullscreenControls = true,
  showDownloadControls = true,

  isFullscreen = false,
  toggleFullscreen = null,

  activeSlideIndex = 0,
  setActiveSlideIndex = null,
}) {
  return (
    <div className={mergeClassNames(styles.gallery, className)} style={style}>
      <ImageContainer
        className={styles.imageContainer}
        slides={slides}
        activeSlideIndex={activeSlideIndex}
        animation={animation}
        objectFit={ObjectFit.contain}
      />

      <div className={styles.buttonContainer}>
        {showButtonControls && (
          <ButtonControls
            className={styles.buttonControls}
            slides={slides}
            activeSlideIndex={activeSlideIndex}
            setActiveSlideIndex={setActiveSlideIndex}
          />
        )}
        {showFullscreenControls && (
          <IconButton
            className={styles.fullscreenButton}
            IconType={isFullscreen ? IconTypes.cancel : IconTypes.fullscreen}
            onClick={toggleFullscreen}
          />
        )}
        {showDownloadControls && (
          <IconButton
            className={styles.downloadButton}
            IconType={IconTypes.download}
            onClick={() => {
              let image = slides[activeSlideIndex].image;
              fetch(image.file.url)
                .then(response => {
                  return response.blob();
                })
                .then(function(blob) {
                  saveAs(blob, image.heading || image.title);
                });
            }}
          />
        )}
        <div className={styles.devider}></div>
        {showArrowControls && (
          <PrevNextControls
            className={styles.prevNextButtons}
            slides={slides}
            currentSlideNum={activeSlideIndex}
            setCurrentSlideNum={setActiveSlideIndex}
          />
        )}
      </div>
      {showTextArea && <TextContainer slides={slides} activeSlideIndex={activeSlideIndex} showHeading={showHeading} />}
    </div>
  );
}
